import { type ReactNode } from "react";
import { Bell, Search, RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/use-auth";

interface HeaderProps {
  readonly onMenuClick: () => void;
  readonly notificationCount?: number;
}

export function Header({
  onMenuClick: _onMenuClick,
  notificationCount = 0,
}: HeaderProps): ReactNode {
  const { user } = useAuth();

  return (
    <header
      className="sticky top-0 z-40 flex h-[60px] shrink-0 items-center gap-3 px-5"
      style={{
        background: "rgba(255,255,255,0.97)",
        backdropFilter: "blur(8px)",
        borderBottom: "1px solid rgba(0,0,0,0.07)",
        boxShadow: "0 1px 0 rgba(0,0,0,0.04)",
      }}
    >
      {/* Right side — store/user info */}
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <div
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-sm font-bold text-white"
          style={{
            background: "linear-gradient(135deg, #7C3AED 0%, #EC4899 100%)",
            fontFamily: "Rubik, sans-serif",
          }}
        >
          {user?.name ? user.name.charAt(0).toUpperCase() : "?"}
        </div>
        <div className="min-w-0">
          <p
            className="text-sm leading-tight truncate"
            style={{
              fontFamily: "Rubik, sans-serif",
              fontWeight: 600,
              color: "#1a1a2e",
            }}
          >
            {user?.name ?? "ניהול העסק"}
          </p>
          {user?.phone && (
            <p
              className="text-[11px] leading-tight truncate"
              style={{ color: "rgba(0,0,0,0.4)" }}
            >
              {user.phone}
            </p>
          )}
        </div>
      </div>

      {/* Left side — action icons */}
      <div className="flex items-center gap-1 shrink-0">
        {/* Search */}
        <button
          type="button"
          className="flex h-9 w-9 items-center justify-center rounded-lg transition-colors"
          style={{ color: "rgba(0,0,0,0.45)" }}
          aria-label="חיפוש"
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLElement).style.background =
              "rgba(124,58,237,0.08)";
            (e.currentTarget as HTMLElement).style.color = "#7C3AED";
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLElement).style.background = "transparent";
            (e.currentTarget as HTMLElement).style.color =
              "rgba(0,0,0,0.45)";
          }}
        >
          <Search className="h-4 w-4" />
        </button>

        {/* Notification bell */}
        <button
          type="button"
          className="relative flex h-9 w-9 items-center justify-center rounded-lg transition-colors"
          style={{ color: "rgba(0,0,0,0.45)" }}
          aria-label="התראות"
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLElement).style.background =
              "rgba(124,58,237,0.08)";
            (e.currentTarget as HTMLElement).style.color = "#7C3AED";
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLElement).style.background = "transparent";
            (e.currentTarget as HTMLElement).style.color =
              "rgba(0,0,0,0.45)";
          }}
        >
          <Bell className="h-4 w-4" />
          {notificationCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-0.5 -end-0.5 h-4 min-w-[16px] px-0.5 text-[10px] flex items-center justify-center"
            >
              {notificationCount > 99 ? "99+" : notificationCount}
            </Badge>
          )}
        </button>

        {/* Refresh */}
        <button
          type="button"
          className="flex h-9 w-9 items-center justify-center rounded-lg transition-colors"
          style={{ color: "rgba(0,0,0,0.45)" }}
          aria-label="רענן"
          onClick={() => window.location.reload()}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLElement).style.background =
              "rgba(124,58,237,0.08)";
            (e.currentTarget as HTMLElement).style.color = "#7C3AED";
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLElement).style.background = "transparent";
            (e.currentTarget as HTMLElement).style.color =
              "rgba(0,0,0,0.45)";
          }}
        >
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>
    </header>
  );
}
